import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';

import type { APIData } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricEditEnsureUniquenessComponent from 'webapp/components/metric/edit/ensure-uniqueness';
import MetricEditHistoricalDataPersistenceComponent from 'webapp/components/metric/edit/historical-data-persistence';
import MetricEditMetricTypePropertiesComponent from 'webapp/components/metric/edit/metric-type-properties';
import QueryBuilderFieldMetricDefinitionBaseComponent from 'webapp/components/query-builder/field/metric-definition-base';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type Source from 'webapp/models-custom/source';

interface Args {
	model: APIData.CustomQuery.MetricDefinitionValue | null;
	source: Source;
	builderType: string;
	onSave: (value: APIData.CustomQuery.MetricDefinitionValue) => void;
	isDisabled: boolean;
	openMetricDefinitionPopupOnInit?: boolean;
}

export default class QueryBuilderFormMetricDefinitionComponent extends QueryBuilderFieldMetricDefinitionBaseComponent<Args> {
	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setInitialData();

		if (this.args.openMetricDefinitionPopupOnInit) {
			this.openMetricDefinitionPopup();
		}
	}

	get model(): APIData.CustomQuery.MetricDefinitionValue | null {
		return this.args.model;
	}

	get source(): Source {
		return this.args.source;
	}

	get isSourceDataset(): boolean {
		return this.args.source.isTypeOfDataset ?? false;
	}

	get isDisabled(): boolean {
		return this.args.isDisabled;
	}

	get isPeriodDataTypeHidden(): boolean {
		return !!this.source.sourceType?.isSalesforce && this.args.builderType === 'query';
	}

	get isAllValuesDataTypeHidden(): boolean {
		return !!this.source.sourceType?.isSalesforce && this.args.builderType === 'builder';
	}

	save(data: APIData.CustomQuery.MetricDefinitionValue): void {
		this.args.onSave(data);
	}
	<template>
		<div
			class="metric-definition"
			{{didUpdate this.updateMetricFromModel @model}}
			{{willDestroy this.cleanupMetric}}
		>
			{{#unless this.isSourceDataset}}
				<div class="item-title">
					<div class="t">DATA AGGREGATION SETTINGS</div>
					<ToolTipWithIconComponent @text={{this.infoTooltipText}} @theme="dark" />
				</div>

				<MetricEditMetricTypePropertiesComponent
					@model={{hash
						metricType=this.normalizedMetricSettingsData.metricType
						aggregationFunction=this.normalizedMetricSettingsData.aggregationFunction
						granulationOrigin=this.normalizedMetricSettingsData.granulationOrigin
					}}
					@submitInProgress={{@isDisabled}}
					@onChange={{this.updateMetricTypeProperties}}
					@hidePeriodDataType={{this.isPeriodDataTypeHidden}}
					@hideAllValuesDataType={{this.isAllValuesDataTypeHidden}}
					@theme="dark"
				/>

				<div class="checkboxes">
					{{#if this.showEnsureUnique}}
						<MetricEditEnsureUniquenessComponent
							@model={{hash
								ensureUnique=this.normalizedMetricSettingsData.ensureUnique
								isAppend=this.normalizedMetricSettingsData.isAppend
							}}
							@sourceType={{this.source.sourceType}}
							@submitInProgress={{@isDisabled}}
							@onChange={{this.changeEnsureUniquenessSettings}}
							@theme="dark"
						/>
					{{/if}}

					{{#if this.showHistoricalDataPersistence}}
						<MetricEditHistoricalDataPersistenceComponent
							@selected={{this.normalizedMetricSettingsData.isHistoricalDataPersistenceEnabled}}
							@submitInProgress={{@isDisabled}}
							@onChange={{this.changeHistoricalDataPersistence}}
							@theme="dark"
						/>
					{{/if}}
				</div>
			{{/unless}}

			<a
				class="advanced-link {{if this.isDisabled 'disabled'}}"
				role="button"
				{{on "click" this.openMetricDefinitionPopup}}
			>
				<CommonUiIconComponent @name="Settings" @size="sm" />Edit metric definition
			</a>
		</div>
	</template>
}
